import { useEffect, useState, type CSSProperties } from "react";
import { getDepositBankRuns, type DepositBankRun } from "../../lib/api";

const fmt = (cents: number | null | undefined) =>
  "$" + ((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtSigned = (cents: number) => (cents > 0 ? "+" : cents < 0 ? "−" : "") + fmt(Math.abs(cents));
const fmtDate = (d: string | null) =>
  d ? new Date(d.slice(0, 10) + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

/** Recorded bank runs (get_deposit_bank_runs), newest first. `refreshKey` reloads after a new run. */
export default function BankRunHistory({ refreshKey }: { refreshKey: unknown }) {
  const [runs, setRuns] = useState<DepositBankRun[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    getDepositBankRuns(24)
      .then((r) => {
        if (!live) return;
        setRuns(r);
        setFailed(false);
      })
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [refreshKey]);

  return (
    <div style={{ marginTop: 24 }}>
      <div style={s.heading}>Bank Runs</div>
      {failed && <div style={s.muted}>Bank runs could not be loaded.</div>}
      {runs && runs.length === 0 && <div style={s.muted}>No bank runs recorded yet.</div>}
      {runs && runs.length > 0 && (
        <div style={s.wrap}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Recorded</th>
                <th style={s.th}>Period</th>
                <th style={s.th}>Expected</th>
                <th style={s.th}>Counted</th>
                <th style={s.th}>Variance</th>
                <th style={s.th}>By</th>
                <th style={s.th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id}>
                  <td style={s.td}>
                    {new Date(r.verified_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  </td>
                  <td style={s.td}>
                    {fmtDate(r.period_start)}
                    {r.period_end && r.period_end !== r.period_start ? ` – ${fmtDate(r.period_end)}` : ""}
                    {r.included_dates ? ` (${r.included_dates.length}d)` : ""}
                  </td>
                  <td style={s.td}>{fmt(r.expected_cents)}</td>
                  <td style={s.td}>{fmt(r.counted_cents)}</td>
                  <td style={{ ...s.td, color: r.variance_cents === 0 ? "var(--green, #27ae60)" : "var(--red, #E05040)" }}>
                    {fmtSigned(r.variance_cents)}
                  </td>
                  <td style={s.td}>{r.verified_by_name || "—"}</td>
                  <td style={s.td} title={r.stale_reason || r.notes || undefined}>
                    {r.status === "stale" ? (
                      <span style={{ color: "var(--red, #E05040)", fontWeight: 700 }}>Needs re-verification</span>
                    ) : (
                      <span style={{ color: "var(--green, #27ae60)", fontWeight: 700 }}>Verified</span>
                    )}
                    {(r.stale_reason || r.notes) && <div style={s.note}>{r.stale_reason || r.notes}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const s: Record<string, CSSProperties> = {
  heading: {
    fontSize: 14,
    fontWeight: 800,
    color: "var(--gold, #F9AF36)",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: 10,
  },
  wrap: {
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 8,
    overflowX: "auto",
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 640 },
  th: {
    background: "rgba(0,0,0,0.3)",
    textAlign: "left",
    padding: "10px 14px",
    color: "var(--gold, #F9AF36)",
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  td: { padding: "10px 14px", color: "var(--text, #F0E6D4)", borderTop: "1px solid rgba(255,255,255,0.03)", verticalAlign: "top" },
  note: { fontSize: 11, color: "var(--muted, #8A7A66)", marginTop: 2, maxWidth: 260, overflowWrap: "anywhere" },
  muted: { fontSize: 13, color: "var(--muted, #8A7A66)" },
};
